import { memo, useCallback, useState } from 'react';

import { Delete } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  FormControl,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { getIn, useFormikContext } from 'formik';
import { useQuery } from 'react-query';
import { getWalletFundClasses } from 'src/services/wallet';
import { Operation } from 'src/types/operation';
import { debounce } from 'src/utils/general';

import { FormikValues, QuotaRecord } from '../AddFundsToWallet';

type QuotaRowProps = {
  fund: Operation;
  fundIndex: number;
  recordIndex: number;
  onRemove: () => void;
  onChange: (field: keyof QuotaRecord, value: string | number) => void;
  canRemove: boolean;
};

export const QuotaRow = memo(function QuotaRow({
  fund,
  fundIndex,
  recordIndex,
  onRemove,
  onChange,
  canRemove,
}: QuotaRowProps) {
  const rowId = `${fund.hashId}-${fundIndex}`;
  const [searchFundQuotaClass, setSearchFundQuotaClass] = useState('');
  const [inputFocusedFundQuotaClass, setInputFocusedFundQuotaClass] =
    useState(false);

  const { values, touched, errors, setFieldValue } =
    useFormikContext<FormikValues>();

  const debouncedSetSearchFundQuotaClass = useCallback(
    debounce((value: string) => {
      setFieldValue(classField, value);
      setSearchFundQuotaClass(value);
    }, 500),
    [],
  );

  const { data: classesFromApi, isLoading: isLoadingClasses } = useQuery({
    queryKey: ['classes', fund.hashId, searchFundQuotaClass, rowId],
    queryFn: () => getWalletFundClasses(fund.cnpj ?? ''),
    enabled: inputFocusedFundQuotaClass && searchFundQuotaClass !== undefined,
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true,
  });

  const classField = `funds[${fundIndex}].quotas[${recordIndex}].class`;
  const classError = getIn(touched, classField) && getIn(errors, classField);
  const seriesField = `funds[${fundIndex}].quotas[${recordIndex}].series`;
  const seriesError = getIn(touched, seriesField) && getIn(errors, seriesField);
  const amountField = `funds[${fundIndex}].quotas[${recordIndex}].amount`;
  const amountError = getIn(touched, amountField) && getIn(errors, amountField);

  const handleClassChange = useCallback(
    (value: string) => {
      setFieldValue(classField, value);
      onChange('class', value);
    },
    [setFieldValue, classField, onChange],
  );

  const handleSeriesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFieldValue(seriesField, value);
      onChange('series', value);
    },
    [setFieldValue, seriesField, onChange],
  );

  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);
      setFieldValue(amountField, value);
      onChange('amount', value);
    },
    [setFieldValue, amountField, onChange],
  );

  return (
    <Box
      display="flex"
      gap={2}
      alignItems="flex-start"
      mb={1}
      flexDirection={{ xs: 'column', sm: 'row' }}>
      <FormControl fullWidth variant="outlined">
        <Autocomplete
          freeSolo
          blurOnSelect
          popupIcon={null}
          loadingText="Carregando subclasses..."
          noOptionsText={<Box>Nenhuma classe encontrada</Box>}
          loading={isLoadingClasses}
          options={
            Array.isArray(classesFromApi)
              ? [...new Set(classesFromApi.map((c) => c.subclasse))]
              : []
          }
          renderOption={(props, option) => (
            <Box {...props} component="li" sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {option}
              </Typography>
            </Box>
          )}
          getOptionLabel={(option) => option}
          onInputChange={(_, value) => {
            debouncedSetSearchFundQuotaClass(value);
          }}
          isOptionEqualToValue={(option, value) => option === value}
          onChange={(_, value) => {
            const newValue = value || '';
            handleClassChange(newValue);
          }}
          value={values?.funds[fundIndex]?.quotas[recordIndex]?.class ?? ''}
          renderInput={(params) => (
            <TextField
              {...params}
              value={values?.funds[fundIndex]?.quotas[recordIndex]?.class ?? ''}
              name={classField}
              size="small"
              label="Classe de cota"
              onFocus={() => {
                setInputFocusedFundQuotaClass(true);
                if (!searchFundQuotaClass) {
                  setSearchFundQuotaClass('');
                }
              }}
              onBlur={() => {
                setInputFocusedFundQuotaClass(false);
              }}
              InputProps={{
                ...params.InputProps,
              }}
              error={Boolean(classError)}
              helperText={classError}
            />
          )}
        />
      </FormControl>
      <FormControl variant="outlined" size="small">
        <TextField
          value={values?.funds[fundIndex]?.quotas[recordIndex]?.series}
          name={seriesField}
          label="Série"
          size="small"
          onChange={handleSeriesChange}
          error={Boolean(seriesError)}
          helperText={seriesError}
        />
      </FormControl>
      <Box display="flex" gap={2} alignItems="center">
        <FormControl variant="outlined" size="small">
          <TextField
            value={values?.funds[fundIndex]?.quotas[recordIndex]?.amount}
            name={amountField}
            size="small"
            label="Qtd de cotas"
            type="number"
            onChange={handleAmountChange}
            inputProps={{ min: 0 }}
            error={Boolean(amountError)}
            helperText={amountError}
          />
        </FormControl>
        <IconButton
          onClick={onRemove}
          size="small"
          color="error"
          disabled={!canRemove}>
          <Delete />
        </IconButton>
      </Box>
    </Box>
  );
});
